import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  AppCheck,
} from 'firebase/app-check';
import { getAuth, Auth, connectAuthEmulator, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getPerformance, FirebasePerformance } from 'firebase/performance';
import { logger } from '../utils/logger';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

declare global {
  interface Window {
    isPlaywright?: boolean;
  }
  // Firebase App Check reads this off `self` to bypass attestation in dev.
  // See https://firebase.google.com/docs/app-check/web/debug-provider
  // eslint-disable-next-line no-var
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;
}

// Initialize Firebase
let app: FirebaseApp;
let appCheck: AppCheck | null = null;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
let performance: FirebasePerformance | null = null;

export function initializeFirebase() {
  // Initialize Firebase app
  app = initializeApp(firebaseConfig);

  // App Check must initialize BEFORE any other Firebase service that will
  // attach tokens (Firestore/Storage/Functions). Skip in Playwright/E2E and
  // when pointed at emulators — both run without a real reCAPTCHA Enterprise
  // site key and would otherwise hard-fail every request.
  const usingEmulators = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true';
  const inPlaywright = typeof window !== 'undefined' && window.isPlaywright;
  const siteKey = import.meta.env.VITE_APPCHECK_SITE_KEY as string | undefined;

  if (!usingEmulators && !inPlaywright) {
    // In DEV, enable the debug-token provider so the local origin can mint a
    // self-signed token that the App Check backend recognises after the dev
    // adds the token via Firebase Console > App Check > Apps > Manage debug
    // tokens. `true` makes the SDK print the generated token to the console.
    if (import.meta.env.DEV) {
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    if (siteKey) {
      try {
        appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true,
        });
        logger.info('Firebase App Check initialized (reCAPTCHA Enterprise)');
      } catch (error) {
        // Don't take the app down if App Check init fails — Stage 2 is
        // monitor-mode only, so callables still serve traffic. Surface loudly
        // so the misconfiguration is obvious in logs.
        logger.error('Failed to initialize Firebase App Check', error as Error);
      }
    } else {
      logger.warn('VITE_APPCHECK_SITE_KEY not set — App Check disabled for this build');
    }
  }

  // Initialize services
  auth = getAuth(app);

  // Set persistence to Local Storage if running in Playwright/E2E
  // This ensures Playwright's storageState captures the auth tokens.
  // Test harness only: localStorage persistence is what signs out an older tab
  // when a new one opens (F-1), so multi-tab specs must not set isPlaywright.
  if (typeof window !== 'undefined' && window.isPlaywright) {
    setPersistence(auth, browserLocalPersistence)
      .catch((error) => logger.error('Failed to set Firebase persistence', error));
  }

  // Cap the long-polling timeout at 25s (default 30s) as a guardrail against
  // transport stalls. The reload-after-write hang itself is fixed by the
  // @firebase/firestore@4.14.1 bump (firebase-js-sdk PR #9842).
  db = initializeFirestore(app, {
    experimentalLongPollingOptions: { timeoutSeconds: 25 },
  });
  storage = getStorage(app);
  functions = getFunctions(app, 'us-central1');

  // Initialize Performance Monitoring (production only)
  if (!import.meta.env.DEV) {
    try {
      performance = getPerformance(app);
      logger.setPerformance(performance);
      logger.info('Firebase Performance Monitoring initialized');
    } catch (error) {
      logger.warn('Failed to initialize Firebase Performance Monitoring', { error });
    }
  }

  // Connect to emulators in development
  if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
    logger.emulator('Connecting to Firebase Emulators...');

    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);

    logger.emulator('Connected to Firebase Emulators');
  }

  return { app, appCheck, auth, db, storage, functions, performance };
}

// Export initialized instances
export { app, appCheck, auth, db, storage, functions, performance };

// Export a default initialization function
export default initializeFirebase;
